import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Row, Spin } from 'antd'
import { isEmpty } from 'lodash'

import {
  DirtyPromptForm,
  FormButtons,
  FormImageUpload,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'

import { useUpdateDishTypeImageDetailsMutation } from '../../hooks'
import { dishTypePropType } from '../../prop-types'
import { setDishTypePageIsDirty } from '../../redux/actions'

import { dishTypeImageFormInitialValues } from './initial-values'

const DishTypeImageDetailsForm = ({ dishType }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.UPDATE_IMAGE_DETAILS,
  ])

  const [
    updateDishTypeImageDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDishTypeImageDetailsMutation()

  const initialValues = useMemo(
    () => dishTypeImageFormInitialValues(dishType),
    [dishType],
  )

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const handleSaveData = useCallback(
    data => updateDishTypeImageDetails(dishType.id, data),
    [dishType.id, updateDishTypeImageDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [
    dishType.imageUrl,
    dishType.iconUrl,
    dishType.processedImageUrl,
    resetForm,
  ])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isImagesDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading || isEmpty(initialValues) ? (
    <Spin />
  ) : (
    <>
      <DirtyPromptForm
        isDirty={isDirty}
        form={form}
        initialValues={initialValues}
        name="dishTypeImageForm"
        onFinish={handleSaveData}
        autoComplete="off"
        onFieldsChange={setToDirty}
      >
        <Row>
          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="image"
            label="Image"
          />

          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="processedImage"
            label="Processed Image"
          />

          <FormImageUpload
            disabled={!isFormEnabled}
            labelAbove
            name="icon"
            label="Icon"
          />
        </Row>
        <FormButtons
          permissions={[PERMISSIONS.DISH_TYPE.UPDATE_IMAGE_DETAILS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </DirtyPromptForm>
    </>
  )
}

DishTypeImageDetailsForm.propTypes = {
  dishType: dishTypePropType.isRequired,
}

export default DishTypeImageDetailsForm
